// ============================================
// pages/Guardian.tsx
// 未成年(<14)发布前家长授权:填家长手机 → 发验证码 → 家长输码 → 授权放行.
// ============================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import Layout from '../components/Layout';
import { callApi } from '../services/api';

export default function Guardian() {
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [devCode, setDevCode] = useState('');   // 开发模式回传的验证码

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const validPhone = /^1[3-9]\d{9}$/.test(phone);

  const sendCode = async () => {
    if (!validPhone) { setError('请输入正确的家长手机号'); return; }
    setBusy(true); setError('');
    const res = await callApi<{ message: string; _devCode?: string }>(
      'community-guardian-send-code',
      { guardian_phone: phone },
    );
    setBusy(false);
    if (!res.ok) { setError(res.message || res.error || '发送失败'); return; }
    setSent(true);
    setCooldown(60);
    if (res._devCode) setDevCode(String(res._devCode));   // 开发模式
  };

  const verify = async () => {
    if (!code.trim()) { setError('请输入验证码'); return; }
    setBusy(true); setError('');
    const res = await callApi<{ publish_enabled: boolean; state: string }>(
      'community-guardian-verify',
      { guardian_phone: phone, code: code.trim() },
    );
    setBusy(false);
    if (!res.ok) { setError(res.message || res.error || '验证失败'); return; }
    navigate('/publish', { replace: true });
  };

  return (
    <Layout width="narrow" showSearch={false}>
      <div className="py-16 flex justify-center">
        <div className="bg-white border border-ink-300/40 rounded-3xl shadow-card p-8 max-w-md w-full">
          <div className="flex justify-center mb-4">
            <span className="grid place-items-center w-14 h-14 rounded-2xl bg-gold-100 text-gold-600">
              <ShieldCheck className="w-7 h-7" strokeWidth={1.5} />
            </span>
          </div>
          <h2 className="text-xl font-bold text-ink-900 text-center mb-2">需要家长同意</h2>
          <p className="text-ink-500 text-sm leading-relaxed mb-6 text-left">
            为保护未满 14 岁的小创客,发布作品前需要家长同意。请填写家长的手机号,我们会发送一条验证码,由家长确认。
          </p>

          {/* 家长手机 */}
          <label className="block text-sm font-medium text-ink-700 mb-1.5">家长手机号</label>
          <div className="flex gap-2 mb-4">
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
              placeholder="请输入家长手机号"
              className="flex-1 px-3 py-2.5 border border-ink-300/60 rounded-xl text-sm"
            />
            <button
              onClick={sendCode}
              disabled={busy || cooldown > 0 || !validPhone}
              className="btn-secondary whitespace-nowrap px-4 disabled:opacity-50"
            >
              {cooldown > 0 ? `${cooldown}s` : (sent ? '重发' : '发送验证码')}
            </button>
          </div>

          {/* 验证码 */}
          {sent && (
            <>
              <label className="block text-sm font-medium text-ink-700 mb-1.5">验证码</label>
              <input
                type="text"
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="请输入 6 位验证码"
                className="w-full px-3 py-2.5 border border-ink-300/60 rounded-xl text-sm mb-2 tracking-widest"
              />
              {devCode && (
                <p className="text-xs text-amber-600 mb-2">
                  开发模式验证码:{devCode}(短信服务未配置时显示,正式发短信后消失)
                </p>
              )}
            </>
          )}

          {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

          {sent && (
            <button onClick={verify} disabled={busy} className="btn-primary w-full mt-2">
              {busy ? '验证中…' : '家长确认同意'}
            </button>
          )}

          <button
            onClick={() => navigate('/')}
            className="w-full text-center text-sm text-ink-400 hover:text-ink-600 mt-4"
          >
            稍后再说,先去逛逛
          </button>
        </div>
      </div>
    </Layout>
  );
}
